import { FormEvent, ReactNode } from "react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";

type PrimaryVariant = "default" | "destructive";

interface BaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  contentClassName?: string;
  children: ReactNode;
  primaryLabel: ReactNode;
  // Shown in the primary button while the action is in flight. Falls back to
  // primaryLabel if omitted.
  primaryPendingLabel?: ReactNode;
  primaryDisabled?: boolean;
  primaryVariant?: PrimaryVariant;
  isPending: boolean;
  cancelLabel?: string;
  showCancel?: boolean;
  showFooterDivider?: boolean;
  // An extra footer button between Cancel and the primary action, e.g. Back in
  // a multi-step dialog.
  secondaryAction?: { label: ReactNode; onClick: () => void };
}

// onSubmit binds the body in a <form> and makes the primary button
// type="submit". onPrimary uses a plain button with onClick. Pick one.
type Props =
  | (BaseProps & { onSubmit: (e: FormEvent) => void; onPrimary?: never })
  | (BaseProps & { onPrimary: () => void; onSubmit?: never });

// Shared shell for nextgen project dialogs: dialog chrome + cancel/primary
// footer + optional <form> wrapper. Bodies stay in the caller.
export const FormDialog = ({
  open,
  onOpenChange,
  title,
  description,
  contentClassName,
  children,
  primaryLabel,
  primaryPendingLabel,
  primaryDisabled,
  primaryVariant = "default",
  isPending,
  cancelLabel = "Cancel",
  showCancel = true,
  showFooterDivider = false,
  secondaryAction,
  onSubmit,
  onPrimary,
}: Props) => {
  const header = (
    <DialogHeader>
      <DialogTitle>{title}</DialogTitle>
      {description && <DialogDescription>{description}</DialogDescription>}
    </DialogHeader>
  );

  const footer = (
    <DialogFooter className={cn(showFooterDivider && "-mx-6 -mt-4 border-t px-6 pt-4")}>
      {showCancel && (
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {cancelLabel}
        </Button>
      )}
      {secondaryAction && (
        <Button
          type="button"
          variant="outline"
          onClick={secondaryAction.onClick}
          disabled={isPending}
        >
          {secondaryAction.label}
        </Button>
      )}
      <Button
        type={onSubmit ? "submit" : "button"}
        variant={primaryVariant}
        onClick={onSubmit ? undefined : onPrimary}
        disabled={isPending || primaryDisabled}
      >
        {isPending ? (primaryPendingLabel ?? primaryLabel) : primaryLabel}
      </Button>
    </DialogFooter>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={contentClassName}
        // Passing aria-describedby at all would unlink a rendered DialogDescription.
        {...(!description && { "aria-describedby": undefined })}
      >
        {onSubmit ? (
          <form onSubmit={onSubmit}>
            {header}
            {children}
            {footer}
          </form>
        ) : (
          <>
            {header}
            {children}
            {footer}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
